import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

// import FacebookIcon from 'assets/chat/badgeIcon/Facebook.svg';
// import GmailIcon from 'assets/chat/badgeIcon/Gmail.svg';
import ImapIcon from 'assets/chat/IMail.svg';
import InstagramIcon from 'assets/chat/badgeIcon/Instagram.svg';
import MessengerIcon from 'assets/chat/badgeIcon/Messanger.svg';
import WhatsAppIcon from 'assets/chat/badgeIcon/WhatsApp.svg';
import Configure from 'assets/unifiedCommunication/Configure.svg';
import Loading from 'common-ui/loading';
import Modal from 'common-ui/modal';
import MyCheckbox from 'common-ui/myCheckbox';
import {
  PERMISSION_TYPES,
  getSocialPermissionsByRole,
  getSocialPermissionsByTeam,
  getSocialPermissionsByUser,
  setSocialPermissions,
} from 'features/chat/chatActions';
import {
  selectRolePermissions,
  selectRolePermissionsLoading,
  selectSavingPermissions,
  selectTeamPermissions,
  selectTeamPermissionsLoading,
  selectUserPermissions,
  selectUserPermissionsLoading,
} from 'features/chat/chatSlice';

import {
  ChannelCell,
  ChannelIcon,
  ChannelIconWrapper,
  ChannelTable,
  CheckboxGroup,
  CheckboxWrapper,
  ConfigureDescription,
  ConfigureHeader,
  ConfigureIcon,
  ConfigureTitle,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from './ChannelConfigurator.style';

// Valid platforms from API: facebook (displayed as Messenger), instagram, whatsapp, gmail, imap
const CHANNELS = [
  { id: 'whatsapp', name: 'WhatsApp', icon: WhatsAppIcon, platform: 'whatsapp' },
  { id: 'facebook', name: 'Messenger', icon: MessengerIcon, platform: 'facebook' },
  { id: 'instagram', name: 'Instagram', icon: InstagramIcon, platform: 'instagram' },
  // { id: 'gmail', name: 'Gmail', icon: GmailIcon, platform: 'gmail' },
  { id: 'imap', name: 'IMAP', icon: ImapIcon, platform: 'imap', bg: '#0277FF' },
];

const API_TO_LOCAL_PERMISSION = {
  none: 'none',
  view_only: 'view',
  view_and_answer: 'reply',
};

const ChannelConfigurator = ({
  isOpen,
  onClose,
  role,
  channelPermissions,
  setChannelPermissions,
  accessType = 'role',
}) => {
  const dispatch = useDispatch();
  const isSaving = useSelector(selectSavingPermissions);

  const roleLoading = useSelector(selectRolePermissionsLoading);
  const teamLoading = useSelector(selectTeamPermissionsLoading);
  const userLoading = useSelector(selectUserPermissionsLoading);
  const rolePermissionsData = useSelector(selectRolePermissions);
  const teamPermissionsData = useSelector(selectTeamPermissions);
  const userPermissionsData = useSelector(selectUserPermissions);

  const isLoading =
    accessType === 'role' ? roleLoading : accessType === 'team' ? teamLoading : userLoading;

  const [allView, setAllView] = useState(false);
  const [allViewReply, setAllViewReply] = useState(true);
  const [existingPermissionId, setExistingPermissionId] = useState(null);

  const getTargetKey = () => {
    if (accessType === 'role') return role?.role;
    return role?.id || role?.uuid;
  };

  const getDisplayName = () => {
    if (accessType === 'role') return role?.role;
    if (accessType === 'team') return role?.team;
    return role?.fullName || role?.name;
  };

  const targetKey = getTargetKey();
  const displayName = getDisplayName();

  useEffect(() => {
    if (isOpen && targetKey) {
      setExistingPermissionId(null);

      if (accessType === 'role') {
        dispatch(getSocialPermissionsByRole({ roleName: targetKey }));
      } else if (accessType === 'team') {
        dispatch(getSocialPermissionsByTeam({ teamId: targetKey }));
      } else if (accessType === 'user') {
        dispatch(getSocialPermissionsByUser({ userId: targetKey }));
      }
    }
  }, [isOpen, targetKey, accessType, dispatch]);

  const getPermissionsData = () => {
    if (accessType === 'role') return rolePermissionsData[targetKey];
    if (accessType === 'team') return teamPermissionsData[targetKey];
    return userPermissionsData[targetKey];
  };

  useEffect(() => {
    if (isOpen && targetKey) {
      const apiData = getPermissionsData();
      if (apiData === undefined) return;

      const newPermissions = {};
      CHANNELS.forEach((channel) => {
        newPermissions[channel.id] = 'none';
      });

      let permissionsArray = [];
      let permissionId = null;

      if (accessType === 'user') {
        permissionsArray = apiData?.permissions || [];
        permissionId = apiData?._id || null;
      } else {
        if (Array.isArray(apiData) && apiData.length > 0) {
          permissionsArray = apiData[0]?.permissions || [];
          permissionId = apiData[0]?._id || null;
        }
      }

      setExistingPermissionId(permissionId);

      permissionsArray.forEach((perm) => {
        const channel = CHANNELS.find((c) => c.platform === perm.platform);
        if (channel) {
          if (perm.isDenied) {
            newPermissions[channel.id] = 'none';
          } else {
            newPermissions[channel.id] = API_TO_LOCAL_PERMISSION[perm.type] || 'none';
          }
        }
      });

      setChannelPermissions(newPermissions);
      updateAllCheckboxState(newPermissions);
    }
  }, [isOpen, targetKey, rolePermissionsData, teamPermissionsData, userPermissionsData]);

  const handleChannelPermission = (channelId, type) => {
    const newPermissions = {
      ...channelPermissions,
      [channelId]: type,
    };
    setChannelPermissions(newPermissions);
    updateAllCheckboxState(newPermissions);
  };

  const updateAllCheckboxState = (permissions) => {
    const allChannelIds = CHANNELS.map((c) => c.id);
    const allAreView = allChannelIds.every((id) => permissions[id] === 'view');
    const allAreReply = allChannelIds.every((id) => permissions[id] === 'reply');

    setAllView(allAreView);
    setAllViewReply(allAreReply);
  };

  const handleAllChange = (type) => {
    const newPermissions = {};
    CHANNELS.forEach((channel) => {
      newPermissions[channel.id] = type;
    });
    setChannelPermissions(newPermissions);

    setAllView(type === 'view');
    setAllViewReply(type === 'reply');
  };

  const handleSave = async () => {
    const permissions = CHANNELS.filter((channel) => {
      const localPermission = channelPermissions[channel.id];
      return localPermission && localPermission !== 'none';
    }).map((channel) => {
      const localPermission = channelPermissions[channel.id];
      return {
        platform: channel.platform,
        type: PERMISSION_TYPES[localPermission],
        isDenied: false,
      };
    });

    const targetId = targetKey;

    try {
      await dispatch(
        setSocialPermissions({
          accessType,
          targetId,
          permissions,
          permissionId: existingPermissionId,
        })
      ).unwrap();

      onClose();
    } catch (error) {
      console.error('Failed to save permissions:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      footer
      okText={isSaving ? 'Saving...' : 'Save Changes'}
      cancelText="Cancel"
      onOk={handleSave}
      okDisabled={isSaving || isLoading}
      width="512px"
      closeIcon
      className="custom-channel-modal"
    >
      {isLoading ? (
        <Loading />
      ) : (
        <div>
          <ConfigureHeader>
            <ConfigureIcon src={Configure} alt="configure" />
            <ConfigureTitle>Configure Channel Access</ConfigureTitle>
          </ConfigureHeader>
          <ConfigureDescription>
            Set channel-level permissions for {displayName}
          </ConfigureDescription>

          <CheckboxWrapper>
            <CheckboxGroup>
              <MyCheckbox
                selected={allView}
                onClick={() => handleAllChange('view')}
                label="All View"
              />
              <MyCheckbox
                selected={allViewReply}
                onClick={() => handleAllChange('reply')}
                label="All View and Reply"
              />
            </CheckboxGroup>
          </CheckboxWrapper>

          <ChannelTable>
            <TableHeader>
              <TableHeaderCell>Channel</TableHeaderCell>
              <TableHeaderCell>None</TableHeaderCell>
              <TableHeaderCell>View</TableHeaderCell>
              <TableHeaderCell>View and Reply</TableHeaderCell>
            </TableHeader>
            {CHANNELS.map((channel) => (
              <TableRow key={channel.id}>
                <TableCell>
                  <ChannelCell>
                    {channel.id === 'imap' ? (
                      <ChannelIconWrapper $bg={channel.bg}>
                        <img src={channel.icon} alt={channel.name} />
                      </ChannelIconWrapper>
                    ) : (
                      <ChannelIcon src={channel.icon} alt={channel.name} />
                    )}
                    {channel.name}
                  </ChannelCell>
                </TableCell>
                <TableCell>
                  <MyCheckbox
                    selected={channelPermissions[channel.id] === 'none'}
                    onClick={() => handleChannelPermission(channel.id, 'none')}
                    disabled={isSaving}
                  />
                </TableCell>
                <TableCell>
                  <MyCheckbox
                    selected={channelPermissions[channel.id] === 'view'}
                    onClick={() => handleChannelPermission(channel.id, 'view')}
                    disabled={isSaving}
                  />
                </TableCell>
                <TableCell>
                  <MyCheckbox
                    selected={channelPermissions[channel.id] === 'reply'}
                    onClick={() => handleChannelPermission(channel.id, 'reply')}
                    disabled={isSaving}
                  />
                </TableCell>
              </TableRow>
            ))}
          </ChannelTable>
        </div>
      )}
    </Modal>
  );
};

export default ChannelConfigurator;
